import CreateIcon from '@mui/icons-material/Create';
import {
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tooltip,
} from '@mui/material';
import { darken, Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import {
  GridRenderCellParams,
  GridSortModel,
  GridValueFormatterParams,
} from '@mui/x-data-grid';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as NavLink, Route, Switch } from 'react-router-dom';
import { useRecoilValueLoadable, useSetRecoilState } from 'recoil';

import {
  DataTable,
  IColDef,
} from 'src/app/modules/Manage/pages/queues-table/DataTable';

import { useBusinessPermissions } from '../../common/helperFunctions/useBusinessPermissions';
import { calendarState } from '../../recoil/atoms/calendarState';
import { Role, user } from '../../recoil/atoms/user';
import {
  methodNameEnum,
  refetchToggle,
} from '../../recoil/selectors/makePHPRequest';
import { permissionsSelector } from '../../recoil/selectors/permissionsSelector';
import { replaceAgent } from '../../recoil/selectors/replaceAgent';
import {
  endpointEnum,
  makeAPIRequest,
} from '../../services/rest/makeAPIRequest';
import { AddNewAgentModal } from '../components/modals/AddNewAgentModal';
import dataTableScss from '../modules/Manage/pages/queues-table/data-table.module.css';
import {
  EnhancedTableToolbar,
  ToolbarAddDeleteButton,
} from '../modules/Manage/pages/queues-table/EnhancedTableToolbar';
// import { DashboardTourExtraSpotlights } from './dashboardTour/DashboardTourExtraSpotlights';

import {
  trackAddAgent,
  trackEditManageProducts,
} from '../../common/userTracking';
import ManageAgentProductsModal from '../components/modals/ManageAgentProductsModal';
import { useFeatureFlag } from '../hooks/useFeatureFlag';

const useStyles = makeStyles((theme: Theme) => ({
  dropdown: {
    marginLeft: '1vw',
    marginTop: '10px',
  },
  userStatusDropdown: {
    marginLeft: '1vw',
    marginTop: '10px',
  },
  userTierWidth: {
    width: '150px !important',
    minWidth: '150px !important',
  },

  dealButton: {
    backgroundColor: 'rgb(98,93,236)',
    '&:hover': {
      backgroundColor: darken(
        'rgb(98,93,236)',
        theme.palette.mode === 'light' ? 0.2 : 0
      ),
    },
    '&:focus': {
      backgroundColor: darken(
        'rgb(98,93,236)',
        theme.palette.mode === 'light' ? 0.2 : 0
      ),
    },
  },
}));
export function ManageAgents(props) {
  const classes = useStyles();
  const setCalendarDisplay = useSetRecoilState(calendarState);
  const replacedAgent = useRecoilValueLoadable(replaceAgent);
  const userAtom = useRecoilValueLoadable(user);
  const permissionsLoadable = useRecoilValueLoadable(permissionsSelector);
  const { role } = permissionsLoadable.contents;
  const isSuperAdminBoolean = role === Role.SUPERADMIN;
  const isManagerBoolean = role === Role.MANAGER;
  const isAdminBoolean = role === Role.ADMIN;
  // const isSuperAdminBoolean = userAtom?.contents?.user?.role === 'superadmin';
  // const isManagerBoolean = userAtom?.contents?.user?.role === 'manager';
  // const isAdminBoolean = userAtom?.contents?.user?.role === 'admin';
  const [activeStatus, setActiveStatus] = useState('all');
  // for modal view
  const [show, setShow] = useState(false);
  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);
  // state for notifications
  // const BusinessUsers = useRecoilValueLoadable(
  //   makePHPRequest({ methodName: methodNameEnum.GET_BUSINESS_USERS })
  // );
  // const users = BusinessUsers?.contents?.data?.result || [];
  // refetch toggle to refresh data manully upon invocation
  const dataUsers = {
    call: methodNameEnum.GET_BUSINESS_USERS,
  };
  const BusinessUsers = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: endpointEnum.GET_BUSINESS_USERS,
      payloadJsonString: JSON.stringify(dataUsers),
    })
  );
  const users = useMemo(
    () =>
      BusinessUsers.state === 'hasValue'
        ? BusinessUsers.contents?.response?.data?.result ?? []
        : [],
    [BusinessUsers]
  );
  const setRefetchToggleForAgents = useSetRecoilState(
    refetchToggle(endpointEnum.GET_BUSINESS_USERS)
  );

  const rows = useMemo(
    () =>
      users
        .filter((user) =>
          activeStatus === 'all'
            ? true
            : activeStatus === 'active'
            ? user.isactive === 1
            : user.isactive === 0
        )
        .map((member) => ({
          ...member,
          userid: +member.userid, // cast to number for sorting
          id: member.userid,
          fullname: `${member.fname} ${member.lname}`,
          userTier:
            member.userTier === 'Enterprise'
              ? 'Professional'
              : member.userTier === 'Enterprise Unlimited'
              ? 'Professional Unlimited'
              : member.userTier === 'payperuse'
              ? 'Free'
              : member.userTier,
        })) || [],
    [activeStatus, users]
  );

  useEffect(() => {
    setCalendarDisplay('none');

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (replacedAgent?.contents?.status === 200) {
      setRefetchToggleForAgents({});
    }
  }, [replacedAgent, setRefetchToggleForAgents]);
  useEffect(() => {
    isManagerBoolean && setActiveStatus('active');
    isAdminBoolean && setActiveStatus('active');
  }, [userAtom]);
  const nameFormatter = (params: GridRenderCellParams) => {
    const name = `${params.row.fname} ${params.row.lname}`;
    return (
      <NavLink
        style={{ color: '#0098C7' }}
        // onClick={() => viewAction(params.row)}
        to={`/manage/agents/${params.row.userid}/edit`}
        data-pup="agent"
      >
        {name}
      </NavLink>
    );
  };
  const formatter = (params: GridRenderCellParams) => {
    return (
      <Tooltip title="Edit Agent">
        <NavLink
          // onClick={() => viewAction(params.row)}
          to={`/manage/agents/${params.row.userid}/edit`}
        >
          <IconButton name="edit" size="large">
            <CreateIcon color="primary" />
          </IconButton>
        </NavLink>
      </Tooltip>
    );
  };
  const capitalize = (params: GridRenderCellParams) => {
    return (
      (params.value as string)?.charAt(0).toUpperCase() +
      (params.value as string)?.slice(1)
    );
  };
  const powerDialerFormatter = (params: GridRenderCellParams) => {
    let tier;
    switch (params.row.powerlistProductId) {
      case 30:
        tier = 1;
        break;
      case 31:
        tier = 4;
        break;
      case 32:
        tier = 10;
        break;
      case 44:
        tier = 'AI';
        break;
      default:
        tier = '';
    }
    return tier;
  };
  const activeStatusFormatter = (params: GridValueFormatterParams) =>
    params.value === 1 ? 'Active' : 'Disabled';

  const deviceFormatter = (params: GridValueFormatterParams) =>
    ((params.value as string)?.toLowerCase().includes('webrtc') ||
      (params.value as string)?.toLowerCase().includes('web rtc')) &&
    'PowerCall';
  const inactiveDateFormatter = (params: GridValueFormatterParams) => {
    if (isSuperAdminBoolean && params.row.isactive === 1) {
      return ' ';
    } else {
      return params.value;
    }
  };
  const lpFormatter = (params: GridValueFormatterParams) =>
    params.value === 1 ? 'On' : 'Off';
  const numToYNFormatter = (params: GridValueFormatterParams) =>
    params?.value === 1 ? 'Yes' : 'No';
  const readOnly = useBusinessPermissions().accountReadOnly;
  const columns = [
    // hidden column to enable searching for user id for superadmins
    {
      field: 'id',
      headerName: 'User ID',
      hide: true,
      disableSearch: isManagerBoolean || isAdminBoolean,
      disableExport: true,
    },
    {
      field: 'userid',
      headerName: 'User ID',
      headerClassName: 'superStyle',
      hide: isManagerBoolean || isAdminBoolean,
      disableExport: isManagerBoolean || isAdminBoolean,
      sx: {
        border: '3px solid #ffec61 !important',
      },
      headerStyle: {
        backgroundColor: '#039be5',

        border: '3px solid #ffec61 !important',
      },
    },
    {
      field: 'isactive',
      headerName: 'Active',
      headerClassName: 'superStyle',
      valueFormatter: activeStatusFormatter,
      disableSearch: true,
      disableExport: true,
      hide: isManagerBoolean || isAdminBoolean,
    },
    {
      field: 'inactiveDate',
      headerName: 'Inactive Date',
      valueFormatter: inactiveDateFormatter,
      headerClassName: dataTableScss.fullWidth + ' superStyle',
      cellClassName: dataTableScss.fullWidth,
      hide: isManagerBoolean || isAdminBoolean || activeStatus === 'active',
      disableExport:
        isManagerBoolean || isAdminBoolean || activeStatus === 'active',
    },
    {
      field: 'fullname',
      headerName: 'Name',
      renderCell: nameFormatter,
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
    },
    {
      field: 'email',
      headerName: 'Email',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
    },
    {
      field: 'userTier',
      headerName: 'Tier',
      headerClassName: classes.userTierWidth,
      cellClassName: classes.userTierWidth,
      // headerClassName: dataTableScss.fullWidth,
      // cellClassName: dataTableScss.fullWidth,
    },
    {
      field: 'powerlistProductId',
      headerName: 'PowerDialer Tier',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
      valueFormatter: powerDialerFormatter,
    },
    {
      field: 'userEXT',
      headerName: 'Ext.',
    },
    {
      field: 'type',
      headerName: 'Type',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
      renderCell: capitalize,
    },
    {
      field: 'chromeVersion',
      headerName: 'Chrome Version',
      headerClassName: 'superStyle',
      hide: isManagerBoolean || isAdminBoolean,
      disableExport: isManagerBoolean || isAdminBoolean,
    },
    {
      field: 'version',
      headerName: 'PC Version',
      headerClassName: 'superStyle',
      hide: isManagerBoolean || isAdminBoolean,
      disableExport: isManagerBoolean || isAdminBoolean,
    },
    {
      field: 'crmname',
      headerName: 'CRM',
    },
    {
      field: 'deviceType',
      valueFormatter: deviceFormatter,
      headerName: 'Dialing Device',
      disableSearch: false,
    },
    {
      field: 'localPresenceLite',
      headerName: 'Connection Boost (CB)',
      headerClassName: dataTableScss.fullWidth + ' superStyle',
      cellClassName: dataTableScss.fullWidth,
      disableSearch: true,
      valueFormatter: numToYNFormatter,
      hide: !isSuperAdminBoolean,
      disableExport: !isSuperAdminBoolean,
    },
    {
      field: 'localPresenceOn',
      headerName: 'CB On',
      valueFormatter: lpFormatter,
    },
    {
      field: 'hasPdCallTranscription',
      headerName: 'Conversation Intelligence',
      headerClassName: dataTableScss.fullWidth + ' superStyle',
      cellClassName: dataTableScss.fullWidth,
      valueFormatter: numToYNFormatter,
      hide: !isSuperAdminBoolean,
      disableExport: !isSuperAdminBoolean,
    },
    {
      field: 'ftcBlocking',
      headerName: 'DNC Blocker',
      headerClassName: dataTableScss.fullWidth + ' superStyle',
      cellClassName: dataTableScss.fullWidth,
      valueFormatter: numToYNFormatter,
      hide: !isSuperAdminBoolean,
      disableExport: !isSuperAdminBoolean,
    },
    {
      field: 'action',
      headerName: 'Actions',
      headerClassName: 'superStyle',
      renderCell: formatter,
      hide: readOnly || !isSuperAdminBoolean,
      disableSearch: true,
      enableSorting: false,
      headerAlign: 'center',
      align: 'center',
      width: 120,
    },
  ] /* satisfies */ as IColDef[];
  /**
   * Create cutom toolbar component for table
   * Use useCallback to stop rerendering
   */

  useEffect(() => {
    if (isSuperAdminBoolean) {
      setActiveStatus('active');
    }
  }, [isSuperAdminBoolean]);
  const ToolbarComponent = useCallback(
    (p) => (
      <EnhancedTableToolbar
        className="manageAgentsToolbar"
        {...p}
        dataType="Agents"
        leftComponent={
          isSuperAdminBoolean && (
            <FormControl
              className={classes.userStatusDropdown + ' superStyle'}
              size="small"
              variant="outlined"
            >
              <InputLabel>Active Status</InputLabel>
              <Select
                value={activeStatus}
                onChange={(e) => setActiveStatus(e.target.value as string)}
                label="Active Status"
              >
                <MenuItem value="all">Show All Users</MenuItem>
                <MenuItem value="active">Show Active Users</MenuItem>
                <MenuItem value="inactive">Show Inactive Users</MenuItem>
              </Select>
            </FormControl>
          )
        }
        rightComponent={
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'flex-end',
              alignContent: 'center',
              gap: '8px',
            }}
          >
            {role === Role.SUPERADMIN && (
              <Button
                color="primary"
                className={`superStyle ${classes.dealButton}`}
                variant="contained"
                disableElevation={true}
                onClick={(event: any) => {
                  props?.history?.push('/manage/agents/products');
                  trackEditManageProducts();
                }}
              >
                Manage Products
              </Button>
            )}
            <ToolbarAddDeleteButton
              className="manageAgentsAdd"
              onClickForAdd={() => {
                trackAddAgent();
                handleShow();
              }}
              isLoading={p.isLoading}
            />
          </div>
        }
      />
    ),
    [handleShow, readOnly, props, role, Role]
  );
  const [sortModel, setGridSortModel] = React.useState<GridSortModel>([
    { field: 'fullname', sort: 'asc' },
  ]);

  const { treatment, isReady } = useFeatureFlag('powercall-log');
  console.log(`!!!!!!!!!!! powercall-log treatment: ${treatment}; isReady: ${isReady}`)
  return (
    <>
      {/* <DashboardTourExtraSpotlights /> */}
      <Paper elevation={0}>
        <DataTable
          rows={rows}
          columns={columns}
          ToolbarComponent={ToolbarComponent}
          loading={BusinessUsers.state === 'loading'}
          sortModel={sortModel}
          setGridSortModel={setGridSortModel}
        />
        <AddNewAgentModal
          handleClose={handleClose}
          handleShow={handleShow}
          show={show}
        />
      </Paper>
      <Switch>
        <Route
          path={'/manage/agents/products'}
          render={(props) => (
            <ManageAgentProductsModal
              endpointEnum={endpointEnum}
              show={true}
              handleClose={() => {
                props.history.push('/manage/agents');
              }}
              formikValues={{}}
            />
          )}
        />
      </Switch>
    </>
  );
}
